/** @jsxImportSource preact */
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import * as m from "../../paraglide/messages.js";
import { ConversationPanel } from "../conversation-panel";
import { leavesPage } from "../navigation";
import { deriveTheme } from "../theme";
import type { WidgetView } from "../view-state";
import { characterVariantFor } from "../widget-character";
import { GeneratingText } from "../widget-message";
import { readReplyPreview } from "../widget-panel";
import type { WidgetRootProps } from "../../widget-root";
import type { CharacterReaction } from "../use-character-reaction";
import { Drawer } from "./base-ui.mjs";
import { PEEK_STYLES, PeekBar } from "./peek-bar";
import { useOutsideTap } from "./use-outside-tap";
import { useScrollContainment } from "./use-scroll-containment";

const SWIPE_MOVEMENT_Y = "--drawer-swipe-movement-y";
const SNAP_POINT_OFFSET = "--drawer-snap-point-offset";
/** Base UI 量抽屉高度、算档位偏移要一两帧；最多等这么多帧就按它的值走。 */
const RESTORE_FRAMES = 10;
/** 窥视条淡出的时长，和 PEEK_STYLES 里 [data-leaving] 的过渡一致。 */
const PEEK_LEAVE_MS = 200;

interface MobileDrawerProps extends WidgetRootProps {
  height: number;
  keyboardOpen: boolean;
  reaction?: CharacterReaction;
  /** 仅展示层分镜使用，不进入会话快照。 */
  waitingLabel?: string;
  entrance?: "open" | "restore";
  /** 买家点了会换页的链接、页面还没换走：角色打盹。 */
  leaving?: boolean;
  view?: WidgetView;
  /** 店面吸底购买栏占掉的底部高度，窥视条浮在它上面。 */
  obstruction?: number;
}

/** 临时键盘展开不覆盖正常档位；外壳位置由 WidgetRoot 管理，键盘避让交给 Base UI。 */
export function MobileDrawer({
  height,
  keyboardOpen,
  entrance = "open",
  leaving = false,
  view,
  obstruction = 0,
  ...props
}: MobileDrawerProps) {
  const theme = deriveTheme(props.config.appearance);
  const { muted, panelRadius, surfaceSolid } = theme;
  const appearance = props.config.appearance;
  const host = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const lastSwipeEvent = useRef<Event | null>(null);
  const stored = view?.initial.drawer;
  const [index, setIndex] = useState(stored?.snap ?? 1);
  const [editing, setEditing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  // 换页、刷新时抽屉按离开时的样子直接摆出来：停在窥视条的还是窥视条（设计稿 12a）。
  const [peek, setPeek] = useState(
    entrance === "restore" && props.state.open && stored?.peek === true,
  );
  const peekWaking = useRef(peek);
  const [peekLeaving, setPeekLeaving] = useState(false);
  const [expanding, setExpanding] = useState(false);
  // 挂载时抽屉就开着：Base UI 头一两帧还没量出档位偏移，按偏移 0 画会先整张顶到页面最上面，
  // 再一路滑下来（GON-176）。这段时间用自己算的档位位置顶上，并且不播任何过渡。
  const [restoring, setRestoring] = useState(props.state.open && !peek);
  // 只算真的开着的抽屉：停在窥视条时抽屉是关着的，升回去要照常播上滑，不能当成已经进场。
  const [entered, setEntered] = useState(restoring);
  // 每次买家打开聊天窗都换一份面板，消息按开窗时间轴入场（设计稿 10a）；窥视条升回抽屉不算重开，历史不重放。
  const panelKey = useRef(0);
  const wasOpen = useRef(props.state.open);
  if (props.state.open && !wasOpen.current) panelKey.current += 1;
  wasOpen.current = props.state.open;
  const drawerOpen = props.state.open && !peek;
  useScrollContainment(host, drawerOpen, fullscreen);
  useOutsideTap(host, drawerOpen, () => {
    props.conversation.setOpen(false);
  });
  useEffect(() => {
    if (!fullscreen) return;
    const html = document.documentElement;
    let reservedGutter: string | undefined;
    // Base UI 锁滚动会保留槽位；全屏无需保留，且槽位会裁掉覆盖其上的内容。
    const sync = () => {
      if (
        !html.style.scrollbarGutter.includes("stable") ||
        ![html, document.body].some((element) =>
          /hidden|clip/.test(getComputedStyle(element).overflowY),
        )
      )
        return;
      reservedGutter = html.style.scrollbarGutter;
      html.style.scrollbarGutter = "auto";
    };
    const observer = new MutationObserver(sync);
    observer.observe(html, { attributes: true, attributeFilter: ["style"] });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });
    sync();
    return () => {
      observer.disconnect();
      if (reservedGutter && html.style.scrollbarGutter === "auto")
        html.style.scrollbarGutter = reservedGutter;
    };
  }, [fullscreen]);
  const keyboardExpanded = drawerOpen && editing && keyboardOpen;
  const activeIndex = fullscreen || keyboardExpanded ? 2 : index;
  useEffect(() => {
    // 卸载聚焦节点不保证派发 focusout，重开不能继承临时编辑状态。
    if (!props.state.open) {
      setEditing(false);
      setEntered(false);
      setFullscreen(false);
      setPeek(false);
    }
  }, [props.state.open]);
  useEffect(() => {
    view?.save({ drawer: { snap: index, peek } });
  }, [view, index, peek]);
  const snapPoints = [
    height / 3,
    height / 2,
    fullscreen ? height : Math.max(1, height - 16),
  ];
  const snapPoint = snapPoints[activeIndex] ?? height / 2;
  useLayoutEffect(() => {
    if (!restoring) return;
    let frames = 0;
    let request = requestAnimationFrame(function settle() {
      const element = popup.current;
      const offset = Number.parseFloat(
        element?.style.getPropertyValue(SNAP_POINT_OFFSET) ?? "",
      );
      const expected = element
        ? Math.max(0, element.offsetHeight - snapPoint)
        : Number.NaN;
      if (Math.abs(offset - expected) <= 1 || (frames += 1) >= RESTORE_FRAMES) {
        setRestoring(false);
        return;
      }
      request = requestAnimationFrame(settle);
    });
    return () => {
      cancelAnimationFrame(request);
    };
  }, [restoring]);
  // 沿 10a 的上滑路径升回抽屉：窥视条上的角色原地缩小淡出，抽屉晚 60ms 起步；历史已在，不重放。
  const expand = () => {
    if (!peek) return;
    setPeek(false);
    setPeekLeaving(true);
    setExpanding(true);
    setTimeout(() => {
      setPeekLeaving(false);
    }, PEEK_LEAVE_MS);
  };
  const font = appearance.font ?? "inherit";
  const fontSize = appearance.fontSize ?? "14px";
  const lastReply = [...props.state.messages]
    .reverse()
    .find((message) => message.role === "ai");
  const handle = (
    <div
      role="slider"
      tabIndex={0}
      aria-label={m.widget_drawer_resize()}
      aria-orientation="vertical"
      aria-valuemin={1}
      aria-valuemax={3}
      aria-valuenow={activeIndex + 1}
      aria-disabled={keyboardExpanded || undefined}
      onKeyDown={(event) => {
        if (keyboardExpanded) return;
        let next = index;
        if (event.key === "ArrowUp" || event.key === "ArrowRight") next++;
        else if (event.key === "ArrowDown" || event.key === "ArrowLeft") next--;
        else if (event.key === "Home") next = 0;
        else if (event.key === "End") next = 2;
        else return;
        event.preventDefault();
        setIndex(Math.max(0, Math.min(2, next)));
      }}
      style={{
        position: "absolute",
        top: 0,
        left: 56,
        right: 56,
        zIndex: 1,
        height: 16,
        display: fullscreen ? "none" : "grid",
        color: muted,
        placeItems: "center",
        cursor: "ns-resize",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <span
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: "currentColor",
          opacity: 0.3,
        }}
      />
    </div>
  );
  return (
    <div
      ref={host}
      style={{
        "--gn-drawer-fill": surfaceSolid,
        "--gn-drawer-shell-height": `${String(height)}px`,
        "--gn-drawer-snap": `${String(snapPoint)}px`,
        "--gn-drawer-min-height": `${String(snapPoints[0])}px`,
        "--gn-character-edge": theme.characterEdge,
        "--gn-peek-ink": theme.ink,
        "--gn-peek-muted": theme.muted,
        "--gn-peek-accent": theme.accentText,
      }}
      onClick={(event) => {
        // 在抽屉里点了跳转：抽屉不关，降成窥视条，先让买家看见新页面（设计稿 12a F1）。
        // 必须在这次点击里就写进存储：同站换页可能快过下一帧，等 effect 再写，新页面读到的还是开着。
        if (!drawerOpen || !leavesPage(event)) return;
        view?.save({ drawer: { snap: index, peek: true } });
        peekWaking.current = false;
        setFullscreen(false);
        setPeek(true);
      }}
      onFocusIn={(event) => {
        if (event.target instanceof HTMLTextAreaElement) setEditing(true);
      }}
      onFocusOut={(event) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !host.current?.contains(event.relatedTarget)
        )
          setEditing(false);
      }}
    >
      <style>{PEEK_STYLES}</style>
      <style>{`
        /* 沿用零宽定位外壳，高度仍供 Base UI 计算吸附点；只有 Popup 占据命中区域。
           键盘高度由 VirtualKeyboardProvider 写在本元素上，外壳底边留在布局视口底部才不会抬两次。 */
        .gn-drawer-viewport { position:absolute; inset:0 0 var(--drawer-keyboard-inset, 0px); }
        /* iOS 在键盘动画一开始就给出最终高度，抽屉先到位、键盘还在滑；键盘让出的那段用面板色垫住，否则会闪出店面。 */
        .gn-drawer-viewport::after { content:""; position:absolute; top:100%; left:0; width:var(--gn-viewport-width); height:var(--drawer-keyboard-inset, 0px); background:var(--gn-drawer-fill); pointer-events:none; }
        /* 抽屉开着时店面压暗 18%，只是视觉，不拦触摸：露出的店面照样能滚（设计稿 10a）。点一下收起聊天窗由 useOutsideTap 管，点到的链接照常响应。 */
        .gn-drawer-scrim { position:absolute; left:0; top:0; width:var(--gn-viewport-width); height:var(--gn-drawer-shell-height); background:rgb(28 27 26 / 18%); opacity:0; pointer-events:none; transition:opacity 240ms cubic-bezier(.4,0,.7,.2); }
        .gn-drawer-scrim[data-shown] { opacity:1; transition:opacity 420ms cubic-bezier(.32,.78,.26,1) 140ms; }
        .gn-drawer-scrim[data-expanding] { transition-delay:60ms; }
        /* 只让消息区滚动，避免回弹越界时外层滚动条挤窄消息、触发重新换行。
           底部安全区留白跟键盘高度同源；分开切换时两者差一帧，输入框会先冲过头再弹回。
           --gn-drawer-body-height 只按档位算，不要加回手指拖动的位移：带上位移时每拖 20px 输入区就变 1px，贴底的消息跟着一格一格跳。 */
        .gn-drawer-popup { --gn-composer-bottom-inset:max(0px, env(safe-area-inset-bottom, 0px) - var(--drawer-keyboard-inset, 0px)); --gn-drawer-body-height:calc(var(--gn-drawer-shell-height) - var(--drawer-keyboard-inset, 0px) - max(16px, env(safe-area-inset-top, 0px)) - var(--drawer-snap-point-offset)); --gn-drawer-settle:.28s cubic-bezier(.32,.72,0,1); --gn-drawer-offset:var(--drawer-snap-point-offset); --gn-drawer-movement:var(--drawer-swipe-movement-y); position:absolute; bottom:0; width:var(--gn-viewport-width); height:calc(100% - max(16px, env(safe-area-inset-top, 0px))); outline:none; pointer-events:auto; overflow:visible; overscroll-behavior:contain; box-shadow:0 -8px 32px rgba(0,0,0,.16); transform:translateY(calc(var(--drawer-snap-point-offset) + var(--drawer-swipe-movement-y))); transition:transform .28s cubic-bezier(.32,.72,0,1); }
        /* 动画只推进 Base UI 已注册的位移变量；高度和 transform 同帧计算，避免合成层先于消息排版。 */
        .gn-drawer-popup[data-snap-motion] { transition:--drawer-snap-point-offset var(--gn-drawer-settle), --drawer-swipe-movement-y var(--gn-drawer-settle); }
        .gn-drawer-popup[data-fullscreen] { height:100%; --gn-drawer-body-height:calc(var(--gn-drawer-shell-height) - var(--drawer-keyboard-inset, 0px) - var(--drawer-snap-point-offset)); }
        /* 输入区按 --gn-drawer-body-height 收紧，不要改回容器查询：WebKit 在尺寸容器里插入新消息时，
           先按新行还没算样式的高度排一轮，把贴底的滚动位置夹下去再恢复不了，回复到达时会话会跳一下。 */
        /* 最低档再往下拖是关闭手势：主体不再变矮，整张抽屉原样往下滑；否则输入框会被压扁，留白也跟着逐像素跳。
           下限不超过抽屉自身高度，键盘把抽屉压得比最低档还矮时不撑出去。 */
        .gn-drawer-body { overflow:hidden; border-radius:inherit; height:max(min(var(--gn-drawer-min-height), 100%), calc(100% - var(--gn-drawer-offset) - var(--gn-drawer-movement))); }
        /* 位移只供外壳计算高度，阻断继承以免每帧重算整段会话的样式。 */
        .gn-drawer-body > * { --gn-drawer-offset:0px; --gn-drawer-movement:0px; }
        /* 消息区靠整像素的 scrollTop 贴底；位移带小数时每帧差半像素，慢拖时文字、气泡和角色会抖。外壳与高度必须取同一个取整值。 */
        @supports (height: round(1.5px, 1px)) {
          .gn-drawer-popup { transform:translateY(round(calc(var(--drawer-snap-point-offset) + var(--drawer-swipe-movement-y)), 1px)); }
          .gn-drawer-body { height:max(round(min(var(--gn-drawer-min-height), 100%), 1px), calc(100% - round(calc(var(--gn-drawer-offset) + var(--gn-drawer-movement)), 1px))); }
        }
        /* 从下沿外 2% 起步，连阴影一起藏住。 */
        .gn-drawer-popup[data-starting-style], .gn-drawer-popup[data-ending-style] { transform:translateY(102%); }
        /* 设计稿 10a：点击后入口先压扁 80ms 再原地缩小淡出，抽屉晚 60ms 起步，420ms 升到档位。从窥视条升回时没有压扁那一拍。 */
        .gn-drawer-popup:not([data-snap-motion]) { transition:transform 420ms cubic-bezier(.32,.78,.26,1) 140ms; }
        .gn-drawer-popup[data-expanding]:not([data-snap-motion]) { transition-delay:60ms; }
        .gn-drawer-popup[data-ending-style] { pointer-events:none; transition:transform 240ms cubic-bezier(.4,0,.7,.2); }
        .gn-drawer-popup[data-ending-style][data-sinking] { transition:transform 260ms cubic-bezier(.4,0,1,1); }
        .gn-drawer-popup[data-swiping] { transition:none; }
        /* Base UI 还没算出档位偏移时，按自己算的档位位置摆（100% 是抽屉自身高度）；两个值一致后才交回。 */
        .gn-drawer-popup[data-restoring] { transition:none; --gn-drawer-offset:max(var(--drawer-snap-point-offset), calc(100% - var(--gn-drawer-snap))); --gn-drawer-body-height:min(calc(var(--gn-drawer-shell-height) - var(--drawer-keyboard-inset, 0px) - max(16px, env(safe-area-inset-top, 0px)) - var(--drawer-snap-point-offset)), var(--gn-drawer-snap)); transform:translateY(max(var(--drawer-snap-point-offset), calc(100% - var(--gn-drawer-snap)))); }
        @media (prefers-reduced-motion: reduce) {
          .gn-drawer-popup, .gn-drawer-popup[data-snap-motion] { transition:opacity 150ms ease; }
          .gn-drawer-popup[data-starting-style], .gn-drawer-popup[data-ending-style] { transform:translateY(var(--drawer-snap-point-offset)); opacity:0; }
          .gn-drawer-scrim, .gn-drawer-scrim[data-shown] { transition:opacity 150ms ease; }
        }
      `}</style>
      <div
        class="gn-drawer-scrim"
        data-shown={drawerOpen || undefined}
        data-expanding={expanding || undefined}
      />
      <Drawer.Root
        open={drawerOpen}
        modal={fullscreen}
        // 别换成 Base UI 自带的点外面关闭：它在触屏上拖页面超过 10px 就关，焦点离开抽屉也关。
        disablePointerDismissal
        snapPoints={snapPoints}
        snapPoint={snapPoint}
        snapToSequentialPoints
        onOpenChangeComplete={(open) => {
          setEntered(open);
          if (open) setExpanding(false);
        }}
        onSnapPointChange={(point, details) => {
          if (fullscreen || keyboardExpanded) return;
          // 关闭手势由 onOpenChange 处理；取消关闭后的回调不能再降一档。
          if (details.reason === "swipe") {
            if (point === null || lastSwipeEvent.current === details.event)
              return;
            lastSwipeEvent.current = details.event;
            if (details.event.type.endsWith("cancel")) return;
            // 使用原语公开的跟手位移，保留其触控、滚动隔离和回弹逻辑。
            const movement = Number.parseFloat(
              popup.current?.style.getPropertyValue(SWIPE_MOVEMENT_Y) ?? "0",
            );
            if (Number.isFinite(movement) && Math.abs(movement) >= 10) {
              setIndex((current) =>
                Math.max(0, Math.min(2, current - Math.sign(movement))),
              );
            }
            return;
          }
          const next = snapPoints.indexOf(Number(point));
          if (next >= 0) setIndex(next);
        }}
        onOpenChange={(open, details) => {
          if (!open && fullscreen && details.reason === "escape-key") {
            details.cancel();
            setFullscreen(false);
            return;
          }
          if (!open && details.reason === "swipe") {
            const canceled =
              fullscreen ||
              keyboardExpanded ||
              details.event.type.endsWith("cancel");
            if (canceled || index !== 0) {
              details.cancel();
              lastSwipeEvent.current = details.event;
              if (!canceled) setIndex((current) => Math.max(0, current - 1));
              return;
            }
          }
          props.conversation.setOpen(open);
        }}
      >
        <Drawer.VirtualKeyboardProvider>
          <Drawer.Portal container={host}>
            <Drawer.Viewport className="gn-drawer-viewport">
              <Drawer.Popup
                ref={popup}
                className="gn-drawer-popup"
                style={{
                  borderRadius: fullscreen
                    ? 0
                    : `${String(panelRadius)}px ${String(panelRadius)}px 0 0`,
                }}
                data-snap-motion={
                  drawerOpen && entered && !restoring ? "" : undefined
                }
                data-restoring={restoring ? "" : undefined}
                data-expanding={expanding ? "" : undefined}
                data-sinking={peek ? "" : undefined}
                data-fullscreen={fullscreen ? "" : undefined}
                data-base-ui-swipe-ignore={
                  fullscreen || keyboardExpanded ? "" : undefined
                }
                aria-label={appearance.title ?? m.widget_header_title()}
                initialFocus={false}
              >
                <div class="gn-drawer-body">
                  <ConversationPanel
                    key={panelKey.current}
                    {...props}
                    state={{ ...props.state, open: true }}
                    mobile
                    drawerHandle={handle}
                    fullscreen={fullscreen}
                    onToggleFullscreen={() => {
                      setFullscreen((value) => !value);
                    }}
                    entrance={panelKey.current === 0 ? entrance : "open"}
                    leaving={leaving}
                    view={view}
                  />
                </div>
              </Drawer.Popup>
            </Drawer.Viewport>
          </Drawer.Portal>
        </Drawer.VirtualKeyboardProvider>
      </Drawer.Root>
      {props.state.open && (peek || peekLeaving) && (
        <PeekBar
          summary={
            props.state.busy ? (
              <GeneratingText progress={props.state.progress} />
            ) : lastReply ? (
              readReplyPreview(lastReply.text)
            ) : (
              (appearance.welcomeMessage ?? m.widget_empty_title())
            )
          }
          dozing={leaving}
          waking={peekWaking.current}
          leaving={!peek}
          lift={obstruction}
          color={theme.accentFill}
          eyeColor={theme.accentFillFg}
          variant={characterVariantFor(
            appearance.character ?? "solid",
            theme.accentFill,
          )}
          radius={panelRadius}
          font={font}
          fontSize={fontSize}
          onExpand={expand}
          onDismiss={() => {
            props.conversation.setOpen(false);
          }}
        />
      )}
    </div>
  );
}
